import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * Operator login form. Calls `POST /v1/auth/login` with the email +
 * password pair and relies on the browser to persist the returned
 * session cookie. The visual contract mirrors DESIGN.md §6 — centered
 * `surface-container` card on a solid `surface-container-lowest`
 * backdrop, bottom-border-only inputs, primary-container button.
 *
 * On success:
 *   - Populates session context with the user shape ops-api returned
 *     (ops_user_id, email, roles).
 *   - Navigates back to `location.state.from` (the path the guard
 *     bounced from) if present, else `/`.
 *
 * On failure: surfaces the ops-api error code + message inline AND
 * via a toast so the operator notices even after switching tabs.
 */

interface LoginResponse {
  ops_user_id: string;
  email: string;
  roles: readonly string[];
}

interface LocationStateFrom {
  from?: string;
}

export function Login(): JSX.Element {
  const { status, setUser, fetchImpl } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If someone visits /login while already authenticated, drop them
  // back into the shell. This avoids a flash of the form on refresh.
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (email.trim().length === 0 || password.length === 0) {
      setError(t('login.error.empty_fields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await opsApiRequest<LoginResponse>(
        '/v1/auth/login',
        {
          method: 'POST',
          body: { email: email.trim(), password },
        },
        fetchImpl,
      );
      setUser({
        opsUserId: response.ops_user_id,
        email: response.email,
        roles: response.roles,
      });
      const rawState: unknown = location.state;
      const returnTo =
        rawState !== null && typeof rawState === 'object'
          ? (rawState as LocationStateFrom).from
          : undefined;
      navigate(typeof returnTo === 'string' && returnTo.length > 0 ? returnTo : '/', {
        replace: true,
      });
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-container-lowest p-8">
      <div className="w-full max-w-[420px] rounded-xl bg-surface-container p-12 text-on-surface">
        <div className="mb-8 flex flex-col gap-1">
          <span className="text-lg font-black tracking-tighter text-on-surface">
            {t('login.brand')}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
            {t('login.subtitle')}
          </span>
        </div>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('login.email')}
            </span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-email"
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('login.password')}
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-password"
              className={INPUT_CLASS}
            />
          </label>
          {error !== null ? (
            <p
              className="m-0 text-xs font-medium text-error"
              role="alert"
              data-testid="login-error"
            >
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
